import { i18n } from 'i18n';
import { map, omit } from 'ramda';
import {
  ChartParameterDto, ChartParameterGroupDto, ChartParameterRole, PourFactDto
} from 'providers/api/analyticsApi';

/** A user-authored chart, as persisted in Project.DashboardCharts; dashboard tiles reference it by its GUID id. */
export interface ChartSpec {
  id: string;
  name: string;
  type: ChartType;
  /** Dimension or date on the category axis. Unused by scatter. */
  xCat: string | null;
  /** Measure on the x axis. Scatter only. */
  xMeas: string | null;
  /** Up to 3 measures. All must share a unit — one chart, one axis. */
  y: string[];
  /** Dimension that splits y[0] into one series per distinct value. */
  series: string | null;
  /** Dimension keys the viewer can filter on. */
  filters: string[];
  /** filterValues[key] === ALL_FILTER means "no filter". */
  filterValues: Record<string, string>;
  /** aggregation per measure key. */
  agg: Record<string, AggregationType>;
  stacked: boolean;
  grain: TimeGrain;
  /** True when xMeas was taken off the y shelf on switching to scatter, so it can be handed back. */
  borrowedX?: boolean;
  /** @deprecated stripped on read */
  hidden?: boolean;
}

export type ChartType = 'column' | 'line' | 'area' | 'scatter';
export type AggregationType = 'sum' | 'avg' | 'min' | 'max';
export type TimeGrain = 'month' | 'quarter';

export const AGGREGATIONS: AggregationType[] = ['sum', 'avg', 'min', 'max'];
export const ALL_FILTER = '__all';

/** Max measures on one chart, and max series a split produces before folding to "Other". */
export const MAX_MEASURES = 3;
export const MAX_SERIES = 4;

export const createEmptySpec = (id: string, name: string): ChartSpec => ({
  id,
  name,
  type: 'column',
  xCat: null,
  xMeas: null,
  y: [],
  series: null,
  filters: [],
  filterValues: {},
  agg: {},
  stacked: false,
  grain: 'month',
});

export const isScatter = (spec: ChartSpec) => spec.type === 'scatter';

/** Scatter reads its x from a measure slot; every other form reads a dimension/date slot. */
export const getX = (spec: ChartSpec) => (isScatter(spec) ? spec.xMeas : spec.xCat);

export type ParameterLookup = Record<string, ChartParameterDto>;

// Parameter labels and group names arrive from the backend ChartParameterCatalog in English.
// Translate them for display; anything the catalogue does not know falls back to the English text.
export const localiseParameterGroups = (groups: ChartParameterGroupDto[]): ChartParameterGroupDto[] => groups.map((group) => ({
  ...group,
  name: group.name ? i18n._(group.name) : group.name,
  items: (group.items ?? []).map((item) => ({ ...item, label: item.label ? i18n._(item.label) : item.label })),
}));

// Registers the ChartParameterCatalog.cs labels with `lingui extract`. Keep in sync with the backend
// catalogue; not meant to be called.
export const chartParameterCatalogMsgIds = () => [
  i18n._('Stope'), i18n._('Level'), i18n._('Area'), i18n._('Stope volume'),
  i18n._('Pour — planned'), i18n._('Pour reference'), i18n._('Pour status'), i18n._('Intended pour date'),
  i18n._('Planned volume'), i18n._('Target UCS'), i18n._('Target mass concentration'),
  i18n._('Design binder content'), i18n._('Design throughput'),
  i18n._('Pour — actual'), i18n._('Pour completed date'), i18n._('Actual volume'),
  i18n._('Actual mass concentration'), i18n._('Actual binder content'), i18n._('Dry tonnage'),
  i18n._('Total binder'), i18n._('Average UCS'), i18n._('Schedule variance'),
];

export const buildLookup = (groups: ChartParameterGroupDto[]): ParameterLookup => {
  const lookup: ParameterLookup = {};
  groups.forEach((group) => group.items.forEach((item) => { lookup[item.key] = item; }));
  return lookup;
};

const factValue = (row: PourFactDto, key: string): unknown => (row as unknown as Record<string, unknown>)[key];

export const readNumber = (row: PourFactDto, key: string): number | null => {
  const value = factValue(row, key);
  return typeof value === 'number' && !Number.isNaN(value) ? value : null;
};

export const readText = (row: PourFactDto, key: string): string | null => {
  const value = factValue(row, key);
  return typeof value === 'string' && value.length > 0 ? value : null;
};

const readDate = (row: PourFactDto, key: string): Date | null => {
  const value = factValue(row, key);
  if (typeof value !== 'string') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Sortable bucket key. Zero-padded so lexical order is chronological. */
const grainKey = (date: Date, grain: TimeGrain) => (grain === 'quarter'
  ? `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`
  : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);

const grainLabel = (key: string, grain: TimeGrain) => {
  const [year, part] = key.split('-');
  if (grain === 'quarter') return `${part} ${year.slice(2)}`;
  return `${MONTHS[Number(part) - 1]} ${year.slice(2)}`;
};

const reduceValues = (values: number[], how: AggregationType): number | null => {
  if (values.length === 0) return null;
  if (how === 'avg') return values.reduce((a, b) => a + b, 0) / values.length;
  if (how === 'min') return Math.min(...values);
  if (how === 'max') return Math.max(...values);
  return values.reduce((a, b) => a + b, 0);
};

export interface ChartSeries {
  name: string;
  unit?: string | null;
  /** Aligned to buckets by index; null = no data in that bucket. */
  values: (number | null)[];
}

export interface ScatterPoint {
  x: number;
  y: number;
  label: string;
}

export interface ScatterSeries {
  name: string;
  points: ScatterPoint[];
}

export interface CategoryChartModel {
  kind: 'category';
  buckets: string[];
  series: ChartSeries[];
  xLabel: string;
  unit?: string | null;
}

export interface ScatterChartModel {
  kind: 'scatter';
  series: ScatterSeries[];
  xLabel: string;
  yLabel: string;
  xUnit?: string | null;
  yUnit?: string | null;
}

export type ChartModel = CategoryChartModel | ScatterChartModel;

export const applyFilters = (rows: PourFactDto[], spec: ChartSpec): PourFactDto[] => rows.filter(
  (row) => spec.filters.every((key) => {
    const wanted = spec.filterValues[key];
    if (!wanted || wanted === ALL_FILTER) return true;
    return readText(row, key) === wanted;
  }),
);

/**
 * Two measures with different units cannot share a value axis. Returns the clashing units when they
 * do, so the builder can say so rather than silently drawing a second y-axis.
 */
export const unitConflict = (spec: ChartSpec, lookup: ParameterLookup): string[] | null => {
  if (spec.series || isScatter(spec) || spec.y.length < 2) return null;
  const units = Array.from(new Set(spec.y.map((key) => lookup[key]?.unit ?? '—')));
  return units.length > 1 ? units : null;
};

export const distinctValues = (rows: PourFactDto[], key: string): string[] => Array.from(
  new Set(rows.map((row) => readText(row, key)).filter((v): v is string => v !== null)),
).sort();

/**
 * Folds fact rows into whatever the spec asks for. This is the whole aggregation engine — it runs in
 * the browser because YesSql's PourIndex projects none of these numeric fields, so there is nothing
 * to group by server-side without first widening the index.
 */
export const buildChartModel = (
  rows: PourFactDto[],
  spec: ChartSpec,
  lookup: ParameterLookup,
): ChartModel | null => {
  const xKey = getX(spec);
  const xParam = xKey ? lookup[xKey] : undefined;
  if (!xKey || !xParam || spec.y.length === 0) return null;

  const filtered = applyFilters(rows, spec);
  const yParam = lookup[spec.y[0]];
  if (!yParam) return null;

  if (isScatter(spec)) {
    const groupNames = spec.series
      ? distinctValues(filtered, spec.series).slice(0, MAX_SERIES)
      : [yParam.label];

    const series = groupNames.map((name) => ({
      name,
      points: filtered
        .filter((row) => !spec.series || readText(row, spec.series as string) === name)
        .map((row) => {
          const x = readNumber(row, xKey);
          const y = readNumber(row, spec.y[0]);
          if (x === null || y === null) return null;
          return { x, y, label: readText(row, 'pourReference') ?? name };
        })
        .filter((p): p is ScatterPoint => p !== null),
    })).filter((s) => s.points.length > 0);

    if (series.length === 0) return null;

    return {
      kind: 'scatter',
      series,
      xLabel: xParam.label,
      yLabel: yParam.label,
      xUnit: xParam.unit,
      yUnit: yParam.unit,
    };
  }

  const isTime = xParam.role === ChartParameterRole.Time;
  const bucketOf = (row: PourFactDto): string | null => {
    if (!isTime) return readText(row, xKey);
    const date = readDate(row, xKey);
    return date ? grainKey(date, spec.grain) : null;
  };

  const keys = Array.from(new Set(filtered.map(bucketOf).filter((k): k is string => k !== null))).sort();
  if (keys.length === 0) return null;
  const buckets = keys.map((k) => (isTime ? grainLabel(k, spec.grain) : k));

  let series: ChartSeries[];

  if (spec.series) {
    const names = distinctValues(filtered, spec.series).slice(0, MAX_SERIES);
    const how = spec.agg[spec.y[0]] ?? 'sum';
    series = names.map((name) => ({
      name,
      unit: yParam.unit,
      values: keys.map((bucketKey) => reduceValues(
        filtered
          .filter((row) => bucketOf(row) === bucketKey && readText(row, spec.series as string) === name)
          .map((row) => readNumber(row, spec.y[0]))
          .filter((v): v is number => v !== null),
        how,
      )),
    }));
  } else {
    series = spec.y.slice(0, MAX_MEASURES).map((measureKey) => {
      const param = lookup[measureKey];
      const how = spec.agg[measureKey] ?? 'sum';
      return {
        name: param?.label ?? measureKey,
        unit: param?.unit,
        values: keys.map((bucketKey) => reduceValues(
          filtered
            .filter((row) => bucketOf(row) === bucketKey)
            .map((row) => readNumber(row, measureKey))
            .filter((v): v is number => v !== null),
          how,
        )),
      };
    });
  }

  return {
    kind: 'category',
    buckets,
    series,
    xLabel: xParam.label,
    unit: yParam.unit,
  };
};

/** Which shelf roles accept which parameter, given the current chart type. */
export const shelfAccepts = (shelf: ShelfId, spec: ChartSpec): ChartParameterRole[] => {
  if (shelf === 'x') {
    return isScatter(spec)
      ? [ChartParameterRole.Measure]
      : [ChartParameterRole.Dimension, ChartParameterRole.Time];
  }
  if (shelf === 'y') return [ChartParameterRole.Measure];
  return [ChartParameterRole.Dimension];
};

export type ShelfId = 'x' | 'y' | 'series' | 'filters';

export const SHELF_CAPACITY: Record<ShelfId, number> = {
  x: 1, y: MAX_MEASURES, series: 1, filters: 2,
};

export const shelfContents = (shelf: ShelfId, spec: ChartSpec): string[] => {
  if (shelf === 'x') { const x = getX(spec); return x ? [x] : []; }
  if (shelf === 'y') return spec.y;
  if (shelf === 'series') return spec.series ? [spec.series] : [];
  return spec.filters;
};

/** Removes a key from whichever shelf holds it. Returns a new spec. */
export const removeFromSpec = (spec: ChartSpec, key: string): ChartSpec => {
  const filterValues = { ...spec.filterValues };
  delete filterValues[key];
  return {
    ...spec,
    xCat: spec.xCat === key ? null : spec.xCat,
    xMeas: spec.xMeas === key ? null : spec.xMeas,
    series: spec.series === key ? null : spec.series,
    y: spec.y.filter((k) => k !== key),
    filters: spec.filters.filter((k) => k !== key),
    filterValues,
  };
};

export const assignToShelf = (
  spec: ChartSpec,
  shelf: ShelfId,
  key: string,
  lookup: ParameterLookup,
): ChartSpec | null => {
  const param = lookup[key];
  if (!param) return null;
  if (!shelfAccepts(shelf, spec).includes(param.role)) return null;
  if (shelfContents(shelf, spec).includes(key)) return null;

  // A parameter lives on one shelf at a time.
  const base = removeFromSpec(spec, key);

  if (shelf === 'x') {
    return isScatter(base) ? { ...base, xMeas: key } : { ...base, xCat: key };
  }
  if (shelf === 'series') return { ...base, series: key };
  if (shelf === 'y') {
    const y = base.y.length >= MAX_MEASURES ? [...base.y.slice(1), key] : [...base.y, key];
    return { ...base, y, agg: { ...base.agg, [key]: base.agg[key] ?? 'sum' } };
  }

  const filters = base.filters.length >= SHELF_CAPACITY.filters
    ? [...base.filters.slice(1), key]
    : [...base.filters, key];
  return { ...base, filters, filterValues: { ...base.filterValues, [key]: ALL_FILTER } };
};

/** Click-to-place: first compatible shelf with room, else first compatible shelf. */
export const autoAssign = (spec: ChartSpec, key: string, lookup: ParameterLookup): ChartSpec | null => {
  const param = lookup[key];
  if (!param) return null;
  const order: ShelfId[] = param.role === ChartParameterRole.Measure
    ? ['y', 'x']
    : ['x', 'series', 'filters'];

  const withRoom = order.find(
    (shelf) => shelfContents(shelf, spec).length < SHELF_CAPACITY[shelf]
      && shelfAccepts(shelf, spec).includes(param.role),
  );
  return assignToShelf(spec, withRoom ?? order[0], key, lookup);
};

/**
 * Switching to scatter needs a measure on x. Borrow the second measure off y rather than dumping the
 * user on an empty chart, and hand it back on the way out so nothing they placed silently vanishes.
 */
export const changeChartType = (spec: ChartSpec, type: ChartType): ChartSpec => {
  const wasScatter = isScatter(spec);
  const next: ChartSpec = { ...spec, type };

  if (isScatter(next) && !next.xMeas && next.y.length > 1) {
    return {
      ...next, xMeas: next.y[0], y: next.y.slice(1), borrowedX: true,
    };
  }
  if (wasScatter && !isScatter(next) && spec.borrowedX && next.xMeas) {
    const y = next.y.length < MAX_MEASURES ? [next.xMeas, ...next.y] : next.y;
    return {
      ...next, y, xMeas: null, borrowedX: false,
    };
  }
  return next;
};

export const formatValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (abs >= 10_000) return `${Math.round(value / 1000)}k`;
  if (abs >= 100) return Math.round(value).toLocaleString('en');
  if (abs >= 10) return value.toFixed(1).replace(/\.0$/, '');
  return value.toFixed(2).replace(/\.00$/, '');
};

export const parseChartSpecs = (json: string | null | undefined): ChartSpec[] => {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? map(omit(['hidden']), (parsed as ChartSpec[]).filter((s) => s && s.id)) : [];
  } catch {
    // A malformed blob must not take the whole dashboard down with it.
    return [];
  }
};
